import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { OnboardingEntryJSON } from "@/types/models";

/**
 * Per-user onboarding progress (guided tour, contextual hints), keyed
 * by flow id (e.g. "tour", "hint:canvas-drag").
 *
 * Signed-in users sync this map with the API (GET/PATCH
 * /users/me/onboarding); demo and anonymous visitors fall back to the
 * "onboarding.state" IndexedDB key via the persistence middleware.
 *
 * Reset recipe (put yourself back into the first-run state):
 *   signed-in:      UPDATE users SET onboarding_state = '{}' WHERE user_id = '<your id>';
 *   demo/anonymous: delete the "onboarding.state" key in devtools →
 *                   Application → IndexedDB → annogram-prefs → kv.
 */
export type OnboardingEntries = Record<string, OnboardingEntryJSON>;

interface OnboardingSliceState {
  state: OnboardingEntries;
  // Session-only: whether the guided tour is running right now, and
  // the step to start on (mid-tour resume). Never persisted — only
  // `state` goes to IndexedDB/the server.
  run: boolean;
  startAt: number;
  // Sample content is being created for a brand-new account. Session
  // state: the durable record is the `sample:seeded` entry in `state`.
  seeding: boolean;
}

const initialState: OnboardingSliceState = {
  state: {},
  run: false,
  startAt: 0,
  seeding: false,
};

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    // Full replace — hydration from IndexedDB or a server GET.
    setOnboardingState: (slice, action: PayloadAction<OnboardingEntries>) => {
      slice.state = action.payload;
    },
    // Key-level merge, mirroring the PATCH endpoint's jsonb semantics:
    // named keys are replaced wholesale, unnamed keys survive.
    mergeOnboardingEntries: (
      slice,
      action: PayloadAction<OnboardingEntries>
    ) => {
      slice.state = { ...slice.state, ...action.payload };
    },
    // Payload: step index to start on (omit for the beginning).
    startTour: {
      reducer: (slice, action: PayloadAction<number>) => {
        slice.run = true;
        slice.startAt = action.payload;
      },
      prepare: (startAt?: number) => ({ payload: startAt ?? 0 }),
    },
    stopTour: (slice) => {
      slice.run = false;
    },
    setSeeding: (slice, action: PayloadAction<boolean>) => {
      slice.seeding = action.payload;
    },
  },
});

export const {
  setOnboardingState,
  mergeOnboardingEntries,
  startTour,
  stopTour,
  setSeeding,
} = onboardingSlice.actions;

export const selectOnboardingState = (root: {
  onboarding: OnboardingSliceState;
}) => root.onboarding.state;

export const selectIsTourRunning = (root: {
  onboarding: OnboardingSliceState;
}) => root.onboarding.run;

export const selectTourStartAt = (root: {
  onboarding: OnboardingSliceState;
}) => root.onboarding.startAt;

export const selectIsSeedingSample = (root: {
  onboarding: OnboardingSliceState;
}) => root.onboarding.seeding;

export default onboardingSlice.reducer;
